import * as _ from 'lodash';
import * as FileSaver from 'file-saver';
import moment, { Moment, MomentInput } from 'moment';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { AppState } from '../../app.state';
import { NotificationType } from '../../shared/models/notification-type';
import { IScheduledPickup } from '../../shared/models/scheduled-pickup.interface';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { NotificationService } from '../../shared/services/notification/notification.service';
import { UserService } from '../../shared/services/user/user.service';
import { SchedulePickupService } from '../services/schedule-pickup.service';
import { SiteConfig } from 'config/site-config';
import { DataTableDirective } from 'angular-datatables';
import { PaginationInstance } from 'ngx-pagination';
import { ShipmentStatus } from 'app/shared/enum/general-enum';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { HowToSchedulePickupDialogComponent } from './how-to-schedule-pickup-dialog/how-to-schedule-pickup-dialog.component';


@Component({
    selector: 'upsc-schedule-pickup',
    templateUrl: './schedule-pickup.component.html',
    styleUrls: ['./schedule-pickup.component.scss'],
})

export class SchedulePickupComponent implements OnInit, OnDestroy {
    //#region Variables
    public searchFormGroup: UntypedFormGroup;
    public noRecordFound: boolean = false;
    public schedulePickupList: IScheduledPickup[];
    public filteredSchedulePickupList: IScheduledPickup[];
    public customMessage: string;
    public CancelPickupWarning: string = 'Are you sure you want to cancel this pickup?';
    public isEUUser: boolean;

    public dtOptions: any = {};
    public dtTrigger = new Subject();

    @ViewChild('CustomInformation') customInfo: ElementRef;
    @ViewChild('wrapper') wrapper: ElementRef;

    @ViewChild(DataTableDirective, {static: false})
    public dtElement: DataTableDirective;

    private isEmpty = true;
    public dateRangeFormGroup: UntypedFormGroup;
    public customDateRangeFormGroup: UntypedFormGroup;
    public dateRangeAll = 0;
    public dateRangeCustom = -1;
    public defaultStartDate = moment().subtract(1, 'days').startOf('day').toDate();
    public defaultEndDate = moment().toDate();
    public formattedStartDate = moment().subtract(1, 'days').startOf('day').format('MM/DD/YYYY');
    public formattedEndDate = moment().format('MM/DD/YYYY');
    public predefinedDayRanges = [1, 7, 15, 30, 60];
    private currentPredefinedDateRange = 1;
    public tablePageSize = 25;
    public tablePageIndex = 1;
    public paginate: PaginationInstance;
    private totalItems = 0;
    private parameterDateFormat = 'YYYY-MM-DD';
    public isLoading = false;
    public maxEndDate = moment().add(7, 'days').toDate();
    //#endregion

    //#region Methods
    public constructor(private schedulepickupservice: SchedulePickupService,
                       private userService: UserService,
                       private dialogService: DialogService,
                       private notificationService: NotificationService,
                       private translate: TranslateService,
                       private formBuilder: UntypedFormBuilder,
                       private readonly appState: AppState,
                       private readonly dialog: MatDialog
    ) {
        this.GetUserCountryCode();
        this.GetSchedulePickups();

        this.translate.onLangChange.subscribe(() =>{
            this.updateDTOptionsLanguage();
        })
    }

    public ngOnInit() {
        this.dtOptions = SiteConfig.tableConfig;
        this.dtOptions.language = {
            emptyTable: this.translate.instant('dashboard.noShipments'),
          };

        this.searchFormGroup = this.formBuilder.group({
            keyword: [''],
        });

        this.searchFormGroup.controls.keyword.valueChanges.subscribe(value => {
            if (!value) {
                this.GetSchedulePickups();
            }
        });

        this.dateRangeFormGroup = this.formBuilder.group({
            dayCount: [this.currentPredefinedDateRange],
        });
    
        this.dateRangeFormGroup.controls.dayCount.valueChanges
            .subscribe(
                (value) => {
                    this.currentPredefinedDateRange = +value;
                    this.tablePageIndex = 1;
        
                    if (+value === -1) {
                    return;
                    }
        
                    this.onDateRangeChanged(+value);
                },
            );
    
        this.customDateRangeFormGroup = this.formBuilder.group({
            startDate: [this.defaultStartDate],
            endDate: [this.defaultEndDate],
        });
    
        this.customDateRangeFormGroup.controls.startDate.valueChanges
            .subscribe(
                (value) => {
                        this.formattedStartDate = moment(value).format('MM/DD/YYYY');
            
                        if (!this.customDateRangeFormGroup.controls.endDate.value) {
                            return;
                        }
            
                        this.dateRangeFormGroup.controls.dayCount.setValue(-1);
                        this.onCustomDateRangeChanged(value, this.customDateRangeFormGroup.controls.endDate.value);
                    },
                );
    
        this.customDateRangeFormGroup.controls.endDate.valueChanges
            .subscribe(
                (value) => {
                        this.formattedEndDate = moment(value).format('MM/DD/YYYY');
            
                        if (!this.customDateRangeFormGroup.controls.startDate.value) {
                        return;
                        }
            
                        this.dateRangeFormGroup.controls.dayCount.setValue(-1);
                        this.onCustomDateRangeChanged(this.customDateRangeFormGroup.controls.startDate.value, value);
                    },
                );
    
        this.dateRangeFormGroup.controls.dayCount.setValue(this.currentPredefinedDateRange);
    
        this.searchFormGroup.controls.keyword.valueChanges.subscribe(value => {
            if (!value) {
            this.tablePageIndex = 1;
            this.onDateRangeChanged(this.currentPredefinedDateRange);
            }
        });
    
        this.updatePaginateConfig();
    }


    public ngOnDestroy() {
        this.dtOptions.searching = false;
    }

    onMenuToggle() {
        if (!this.wrapper.nativeElement.classList.contains('toggled')) {this.wrapper.nativeElement.className = this.wrapper.nativeElement.className + ' toggled';} else {this.wrapper.nativeElement.classList.remove('toggled');}
    }

    public GetSchedulePickups(fromDate: string = null, toDate: string = null, trackingNumber: string = null) {
        this.isLoading = true;

        this.schedulepickupservice.getScheduledPickups(fromDate, toDate, trackingNumber)
            .subscribe(
                (response) => {
                    this.schedulePickupList = response;

                    if (!response) {
                        this.isEmpty = true;
                        this.filteredSchedulePickupList = [];
                        this.dtTrigger.next(null);
                        this.isLoading = false;
                        return;
                    }

                    this.isEmpty = response.length === 0;
                    this.noRecordFound = this.isEmpty;
                    this.totalItems = +this.schedulePickupList?.[0]?.TotalItems;
                    this.updatePaginateConfig();

                    this.schedulePickupList.forEach((pickup) => {
                        pickup.PickUpDate = moment(pickup?.PickUpDate).format('MM-DD-YYYY');

                        const parsedReadyAt = pickup?.ReadyAt?.slice(0, 2) + ':' + pickup?.ReadyAt?.slice(2);
                        const parsedPickUpBy = pickup?.PickUpBy?.slice(0, 2) + ':' + pickup?.PickUpBy?.slice(2);
                        pickup.PickUpTime = `${parsedReadyAt}-${parsedPickUpBy}`;

                        pickup.PickUpAddress = `${pickup?.ShipFromCompanyName} ${pickup?.ShipFromAddress1}, ${pickup?.ShipFromCity}, ${pickup?.ShipFromState} ${pickup?.ShipFromZip}`;
                        pickup.ShipToAddress = `${pickup?.ShipToAddress1}, ${pickup?.ShipToCity}, ${pickup?.ShipToState} ${pickup?.ShipToZip}`;
                        
                        if (pickup?.TrackingNumber?.length > 0) {
                            switch (pickup?.CarrierCode?.toUpperCase()) {
                                case 'UPS':
                                    pickup.TrackingNumberLink = `https://www.ups.com/track?loc=en_US&requester=QUIC&tracknum=${pickup?.TrackingNumber}/trackdetails`;
                                    break;
                                case 'FEDEX':
                                    pickup.TrackingNumberLink = `https://www.fedex.com/fedextrack/?trknbr=${pickup?.TrackingNumber}`;
                                    break;
                                default:
                                    break;
                            }
                        }

                        pickup.CarrierStatus = ShipmentStatus?.[pickup?.Status];
                    });
                    
                    this.filteredSchedulePickupList = _.cloneDeep(this.schedulePickupList);

                    this.dtTrigger.next(null);
                    this.isLoading = false;
                },
                (error) => {
                    this.dtTrigger.next(null);
                    this.isLoading = false;
                }
            )
    }

    confirmVoidPickup(shipmentId: string) {
        this.dialogService.confirm('Void Confirmation', this.CancelPickupWarning).subscribe(isConfirmed => {
            if (isConfirmed) {this.voidPickup(shipmentId);}
        });
    }

    voidPickup(shipmentId: string) {
        this.schedulepickupservice.deleteSchedulePickupQuote(shipmentId).subscribe(_response => {
            this.notificationService.notify('Pickup successfully canceled.', 'Voided Successfully', NotificationType.SUCCESS);
            const keyword = this.searchFormGroup.controls['keyword'].value;
            this.GetSchedulePickups();
        });
    }

    public onSearchSubmit(event, form) {
        event.preventDefault();
        this.GetSchedulePickups(this.formattedStartDate, this.formattedEndDate, form?.keyword);
    }

    public updateDTOptionsLanguage(): void{
        this.dtOptions.language = {
                emptyTable: this.translate.instant('dashboard.noShipments')
            };
    
        this.rerender();
    }

    private rerender(): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            // Destroy the table first
            if(this.isEmpty) {
                dtInstance.destroy();
            }
            // Call the dtTrigger to rerender again
            this.dtTrigger.next(null);
        });
      }

    private GetUserCountryCode() {
        const user = this.appState.user$();
        this.isEUUser = this.userService.isEUUser(user?.CountryCode);
    }

    public onDateRangeChanged(dayCount: number) {
        let dateRangeStart = moment().subtract(dayCount, 'days').startOf('day');
        let dateRangeEnd = moment().add(7, 'days').startOf('day');

        switch (dayCount) {
            case -1:
                dateRangeStart = moment(this.customDateRangeFormGroup.controls.startDate.value).startOf('day');
                dateRangeEnd = moment(this.customDateRangeFormGroup.controls.endDate.value).endOf('day');
                break;
            default:
                break;
        }

        if (![-1, 0].includes(dayCount)) {
            this.setCustomDateRangeValues(dateRangeStart, dateRangeEnd, false);
        }

        switch (dayCount) {
            case 0:
                this.GetSchedulePickups();
                break;
            default:
                this.onCustomDateRangeChanged(dateRangeStart, dateRangeEnd);
                break;
        }
    }

    private setCustomDateRangeValues(dateRangeStart: Moment, dateRangeEnd: Moment, isEmitEvent = false) {
        this.customDateRangeFormGroup.patchValue(
        {
            startDate: dateRangeStart.toDate(),
            endDate: dateRangeEnd.toDate(),
        },
        {
            emitEvent: isEmitEvent,
        });

        this.formattedStartDate = dateRangeStart.format('MM/DD/YYYY');
        this.formattedEndDate = dateRangeEnd.format('MM/DD/YYYY');
    }

    public onCustomDateRangeChanged(startDateString: MomentInput, endDateString: MomentInput) {
        // Convert to start of day and end of day respectively to include the entire day in the filter
        const fromDate = moment(startDateString).startOf('day').format(this.parameterDateFormat);
        const toDate = moment(endDateString).endOf('day').format(this.parameterDateFormat);
        this.GetSchedulePickups(fromDate, toDate);
        // this.dtTrigger.next(null);
    }

    private updatePaginateConfig() {
        this.paginate = {
            totalItems: this.totalItems,
            currentPage: this.tablePageIndex,
            itemsPerPage: this.tablePageSize,
        };
    }

    public download() {
        const columns = [
            'TrackingNumber', 'InsuredValue', 'ConfirmationNumber', 'DispatchConfirmationNumber',
            'CarrierStatus', 'PickUpAddress', 'PickUpContactName', 'PickUpTelNo', 'PickUpDate',
            'PickUpTime', 'ShipFromCompanyName', 'ShipFromFirstName', 'ShipFromLastName',
            'ShipFromAddress1', 'ShipFromAddress2', 'ShipFromCity', 'ShipFromState', 'ShipFromZip',
            'ShipFromCountry', 'ShipFromPhone', 'ShipFromEmail', 'ShipToCompanyName', 'ShipToFirstName',
            'ShipToLastName', 'ShipToAddress1', 'ShipToAddress2', 'ShipToCity', 'ShipToState',
            'ShipToZip', 'ShipToCountry', 'ShipToPhone', 'ShipToEmail', 'ShipDate', 'CreateDate'
        ];

        const rows = this.filteredSchedulePickupList.map(item =>
            columns.map(col => {
                const value = item[col] ?? '';
                return /[,\n"]/.test(String(value))
                    ? `"${String(value).replace(/"/g, '""')}"`
                    : value;
            }).join(',')
        );

        const csv = [columns.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const fileName = `scheduled-pickups_${moment().format('MM_DD_YYYY')}.csv`;
        FileSaver.saveAs(blob, fileName);
    }

    public schedulePickup() {
        // TODO
    }

    public openHowToSchedulePickupDialog() {
        const dialogConfig: MatDialogConfig = {
            maxWidth: '100%',
            width: '40rem'
        };
        this.dialog.open(HowToSchedulePickupDialogComponent, dialogConfig);
    }
    //#endregion
}
